import {AliveSession as BaseAliveSession} from '@github/alive-client'
import type {AliveSessionOptions, Notifier} from '@github/alive-client'

type AliveSessionConfig<T> = {
  url: string
  refreshUrl: string
  shared: boolean
  notify: Notifier<T>
  options: AliveSessionOptions
  useVerifiedFetch?: boolean
}

export class AliveSession<T> extends BaseAliveSession<T> {
  #refreshUrl: string
  #useVerifiedFetch: boolean

  constructor({url, refreshUrl, shared, notify, options, useVerifiedFetch = false}: AliveSessionConfig<T>) {
    super(url, () => this.#getUrlFromRefreshUrl(), shared, notify, undefined, options)
    this.#refreshUrl = refreshUrl
    this.#useVerifiedFetch = useVerifiedFetch
  }

  #getUrlFromRefreshUrl() {
    return fetchRefreshUrl(this.#refreshUrl, this.#useVerifiedFetch)
  }
}

type PostUrl = {url?: string; token?: string}
export async function fetchRefreshUrl(url: string, useVerifiedFetch = false): Promise<string | null> {
  if (useVerifiedFetch) {
    return post(url, {'GitHub-Verified-Fetch': 'true'})
  }

  const data = await fetchJSON<PostUrl>(url)
  return data && data.url && data.token ? post(data.url, data.token) : null
}

async function fetchJSON<T>(url: string): Promise<T | null> {
  const response = await fetch(url, {headers: {Accept: 'application/json'}})
  if (response.ok) {
    return response.json()
  } else if (response.status === 404) {
    return null
  } else {
    throw new Error('fetch error')
  }
}

async function post(url: string, headersOrToken: string | Record<string, string>): Promise<string | null> {
  const response = await fetch(url, {
    method: 'POST',
    mode: 'same-origin',
    headers: {
      ...(typeof headersOrToken === 'string' ? {'Scoped-CSRF-Token': headersOrToken} : headersOrToken),
    },
  })
  if (response.ok) {
    return response.text()
  } else {
    throw new Error('fetch error')
  }
}
